import { AfterContentChecked, AfterViewInit, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { scrollSidebarActiveItemIntoView } from './sidebar-scroll.util';
import { UserService } from '../services/user.service';
import { ClientsService } from '../services/clients.service';
import { Client } from '../models/Client';
import { Observable, Subject, of } from 'rxjs';
import { UtilityService } from '../services/utility.service';
import { environment } from 'environments/environment';
import { NavigationEnd, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, filter, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { LoadingService } from 'app/services/loading.service';
import { AvailableFormsService } from 'app/services/available-forms.service';
import { NavService } from 'app/services/nav.service';
import { FeatureFlagService } from 'app/services/feature-flag.service';
import { FEATURE_FLAGS } from 'app/shared/data/feature-flags';
import { ClientSettingsPanelService, buildUnsavedChangesSwalOptions } from 'app/services/client-settings-panel.service';
import Swal from 'sweetalert2';

declare const $: any;

//Metadata
export interface RouteInfo {
	id?: string;
	path: string;
	title: string;
	/** 'sub' groups children; 'external' links out to another origin (href) in a new tab. */
	type: string;
	/** Absolute URL for type 'external' items, such as a standalone hub. */
	href?: string;
	collapse?: string;
	children?: RouteInfo[];
	order?: number;
	enabled?: boolean;
	isLocalSection?: boolean;
}

const misc: any = {
	navbar_menu_visible: 0,
	active_collapse: true,
	disabled_collapse_init: 0,
};

@Component({
	selector: 'sidebar-cmp',
	templateUrl: 'sidebar.component.html',
	styleUrls: ['sidebar.component.scss'],
	standalone: false,
})
export class SidebarComponent implements AfterContentChecked, OnDestroy, OnInit, AfterViewInit {
	private readonly destroy$ = new Subject<void>();

	public menuItems: any[] = [];
	user: any;
	isAdmin: boolean;
	clients: Client[];
	selectedClientId: string;
	/** Last committed selection, used to revert the dropdown when a client switch is cancelled. */
	private previousClientId: string;
	disableClientDD = true;
	private toggleButton;
	private sidebarVisible: boolean;
	private screenSize = '';
	searchForm = new UntypedFormControl();
	filteredForms: any[] = [];
	formGroup: UntypedFormGroup;
	searchText: Observable<string>;
	filteredValues: Observable<any[]>;
	private hasScrolledActiveItem = false;
	private navigationCount = 0;

	constructor(
		private readonly navService: NavService,
		private readonly userService: UserService,
		private readonly clientService: ClientsService,
		private readonly utilityService: UtilityService,
		private readonly element: ElementRef,
		private readonly router: Router,
		private readonly fb: UntypedFormBuilder,
		private loadingService: LoadingService,
		private readonly availableFormsService: AvailableFormsService,
		private readonly featureFlagService: FeatureFlagService,
		private readonly clientSettingsPanelService: ClientSettingsPanelService
	) {
		this.user = this.userService.currentUserValue.user;

		this.selectedClientId = this.user.clientId ?? '';
		this.previousClientId = this.selectedClientId;
		this.isAdmin = this.userService.hasRole('Aclarian Support') || this.userService.isReadOnlyView();
		this.sidebarVisible = false;

		this.formGroup = this.fb.group({
			formSearch: [''], // Add other form controls here if needed
		});
	}

	private loadFormsDropDown() {
		this.searchText = this.searchForm.valueChanges.pipe(startWith(''), debounceTime(500), distinctUntilChanged());
		this.filteredValues = this.searchText.pipe(
			switchMap((val) => {
				return of(this.filterForms(val.trim()));
			})
		);
	}

	private filterForms(query: string): any[] {
		if (!query) {
			return this.filteredForms;
		}
		query = query.toLowerCase();
		const filteredData = JSON.parse(JSON.stringify(this.filteredForms))
			.map((item) => {
				if (item.children) {
					item.children = item.children.filter((child) => child.title.toLowerCase().includes(query));
					return item;
				}
				return null;
			})
			.filter((item) => item !== null && item.children.length > 0);
		return filteredData;
	}

	extractTitleAndPath(items) {
		// External links (standalone hubs, EN-5014) are not portal routes, so the search, which
		// navigates inside the portal, must not offer them.
		return items
			.map((item) => ({ ...item, children: item.children?.filter((child) => child.type !== 'external') }))
			.filter((x) => x.children?.length > 0)
			.map((item) => {
				const parentPath = item.path;
				const children = [];
				if (item.children.length > 0) {
					item.children.forEach((child) => {
						if (child.children) {
							children.push(
								...child.children.map((subChild) => ({
									...subChild,
									path: parentPath + '/' + subChild.path,
								}))
							);
						} else {
							children.push({
								...child,
								path: parentPath + '/' + child.path,
							});
						}
					});
				}

				return {
					title: item.title,
					path: item.path,
					children: children,
				};
			});
	}
	updateMenuItems() {
		if (this.isAdmin) {
			this.menuItems.forEach((x) => {
				if (!x.isLocalSection) {
					x.enabled = true;
					x.children?.forEach((y) => (y.enabled = true));
				}
			});
		}
		this.enableMenus(); // set visibility of menus
		for (const menu of this.menuItems) {
			const childMenus = ['report', 'form', 'data-management'];
			for (let child = 0; child < menu.children?.length; child++) {
				const element = menu.children[child];
				if (childMenus.includes(element.type)) {
					const reportSub = menu.children?.find((t) => t.collapse === element.type);
					if (!reportSub) {
						if (!menu.children) {
							menu.children = [];
						}

						if (element.type === 'report') {
							menu.children.push({
								order: 3,
								path: '/report',
								title: 'Reports',
								type: 'sub',
								collapse: 'report',
								icontype: 'nc-icon nc-single-copy-04',
								children: [element],
							});
						} else if (element.type === 'form') {
							menu.children.push({
								order: 1,
								path: '/form',
								title: 'Forms',
								type: 'sub',
								collapse: 'form',
								icontype: 'nc-icon nc-notes',
								children: [element],
							});
						} else if (element.type === 'data-management') {
							menu.children.push({
								order: 2,
								path: '/data-management',
								title: 'Data Management',
								type: 'sub',
								collapse: 'data-management',
								icontype: 'nc-icon nc-layers-3',
								children: [element],
							});
						}
					} else {
						if (!reportSub.children) {
							reportSub.children = [];
						}
						reportSub.children.push(element);
					}
				}
			}

			menu.children = menu.children
				?.filter((t) => !childMenus.includes(t.type))
				.sort((a, b) => (a.order < b.order ? -1 : 1));
		}
		this.filteredForms = this.extractTitleAndPath(this.menuItems);
		this.loadFormsDropDown();
	}

	ngOnInit() {
		this.featureFlagService
			.isEnabled(FEATURE_FLAGS.OPTIMIZED_AVAILABLE_FORMS)
			.pipe(
				switchMap((enabled) =>
					enabled ? this.availableFormsService.getUserLegacyRouteInfo() : this.navService.routes$
				),
				filter((data) => data && data.length > 0),
				takeUntil(this.destroy$)
			)
			.subscribe((data) => {
				this.menuItems = [...data];
				this.updateMenuItems();
				console.log('Sidebar route info items:', this.menuItems);
			});

		const navbar: HTMLElement = this.element.nativeElement;
		const body = document.getElementsByTagName('body')[0];
		this.toggleButton = navbar.getElementsByClassName('navbar-toggler')[0];
		if (body.classList.contains('sidebar-mini')) {
			misc.sidebar_mini_active = true;
		}

		this.updateMenuItems();
		this.disableClientDD = false;
		if (this.userService.isClientAdmin(this.selectedClientId) && !this.isAdmin && this.user.clients?.length === 0) {
			this.disableClientDD = true;
		} else if (this.user.clients?.length > 1 && !this.isAdmin) {
			this.clientService.getClientList().subscribe((data) => {
				this.clients = data
					.filter((t) => t.isActive)
					.filter((x) => this.user.clients.map((x) => x.clientId).includes(x.id));
			});
		} else if (this.isAdmin) {
			this.clientService.getClientList().subscribe((data) => {
				this.clients = data.filter((t) => t.isActive);
			});
		}

		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
			this.stopAutoScrollAfterInitialNavigation();
			if (event.urlAfterRedirects.startsWith('/')) {
				this.closeSideBar();
			}
		});
		if (window.innerWidth < 991) {
			this.closeSideBar();
		}
	}

	ngAfterContentChecked(): void {
		$('.sub-sub').removeClass('active');
		$('.sub-sub-item.active').parent().parent().prev('.sub-sub').first().addClass('active');
		const elementsToCollapse = document.getElementsByClassName('collapse-menu');
		if (elementsToCollapse.length > 0) {
			for (const element of Array.from(elementsToCollapse)) {
				if (
					this.selectedClientId === environment.lakeHelenId ||
					this.selectedClientId === environment.gladesBOCC ||
					this.selectedClientId === environment.cityOfGroveland
				) {
					element.classList.add('show');
				}
			}
		}
		this.scrollActiveItemIntoViewOnce();
	}

	// The initial navigation is the only one that should auto-scroll; later ones belong to the user.
	stopAutoScrollAfterInitialNavigation(): void {
		this.navigationCount++;
		if (this.navigationCount > 1) {
			this.hasScrolledActiveItem = true;
		}
	}

	scrollActiveItemIntoViewOnce(): void {
		if (this.hasScrolledActiveItem) {
			return;
		}
		const wrapper = this.element.nativeElement.querySelector('.sidebar-wrapper') as HTMLElement | null;
		if (scrollSidebarActiveItemIntoView(wrapper)) {
			this.hasScrolledActiveItem = true;
		}
	}

	ngAfterViewInit() {
		window.addEventListener('resize', () => this.handleWindowResize());
	}

	clientSelected() {
		// AC11: guard switching clients while the settings panel has unsaved edits.
		if (this.clientSettingsPanelService.isOpenAndDirty()) {
			Swal.fire(
				buildUnsavedChangesSwalOptions(
					'You have unsaved changes in Client Settings. Discard them or continue editing?',
					this.clientSettingsPanelService.getUnsavedChangesSummary()
				)
			).then((result) => {
				if (result.isConfirmed) {
					this.clientSettingsPanelService.close();
					this.commitClientSwitch();
				} else {
					// Continue editing: revert the dropdown to the previously selected client.
					this.selectedClientId = this.previousClientId;
				}
			});
			return;
		}
		this.commitClientSwitch();
	}

	private commitClientSwitch() {
		this.previousClientId = this.selectedClientId;
		this.loadingService.OnLoading.emit(true);

		// Reload the current page after switching — components read currentUserValue on init,
		// so the reload re-initializes everything with the new client context.
		if (this.selectedClientId) {
			// Real client selected: get a fresh token, then refresh configuration and reload.
			this.userService.setClientId(
				this.selectedClientId,
				() => {
					this.utilityService.retrieveConfiguration(this.selectedClientId, () => location.reload(), true);
				},
				true
			);
		} else {
			// Cleared selection: persist an empty client id (never the literal "undefined") and
			// skip the token swap. setClientId only invokes its callback when getNewToken is true,
			// so trigger the reload directly here.
			this.userService.setClientId('', undefined, false);
			location.reload();
		}
	}

	enableMenus() {
		this.menuItems
			.filter((x) => x.type === 'sub')
			.forEach((x) => {
				// The second menu publish already moved forms under a group that has no enabled flag.
				x.enabled = x.children?.some((y) => y.enabled || y.children?.some((child) => child.enabled)) || false;
			});
	}

	hasEnabledItem(childitems) {
		return childitems?.children.some((x) => x.enabled);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	sidebarClose() {
		const html = document.getElementsByTagName('html')[0];
		if (this.toggleButton) {
			this.toggleButton.classList.remove('toggled');
		}
		this.sidebarVisible = false;
		html.classList.remove('nav-open');
		const mainPanel = <HTMLElement>document.getElementsByClassName('main-panel')[0];

		if (window.innerWidth < 991) {
			setTimeout(function () {
				mainPanel.style.position = '';
			}, 500);
		}
	}

	minimizeSidebar() {
		const body = document.getElementsByTagName('body')[0];
		const sideBar = document.getElementsByClassName('sidebar')[0];
		const goldenBox = document.getElementsByClassName('golden-box-shape')[0];

		const mainSidebarWrapper = document.getElementsByClassName('sidebar-pointer-events')[0];
		if (misc.sidebar_mini_active === true) {
			body.classList.remove('sidebar-mini');
			sideBar.classList.add('removeSpace');
			goldenBox.classList.add('golden-box-bg');
			misc.sidebar_mini_active = false;
		} else {
			body.classList.add('sidebar-mini');
			misc.sidebar_mini_active = true;
			goldenBox.classList.remove('golden-box-bg');
			mainSidebarWrapper.classList.add('pointer-events-none');
			setTimeout(function () {
				mainSidebarWrapper.classList.remove('pointer-events-none');
			}, 1000);
		}

		this.handleWindowResize();
	}

	handleWindowResize() {
		const specificWidth = 991;
		const windowWidth = window.innerWidth;
		const sideBar = document.getElementsByClassName('sidebar')[0];
		const body = document.getElementsByTagName('body')[0];

		if (windowWidth < specificWidth) {
			if (body.classList.contains('sidebar-mini')) {
				sideBar.classList.remove('removeSpace');
				sideBar.classList.add('addSpace');
			} else {
				if (this.screenSize === undefined || this.screenSize === 'large') {
					body.classList.add('sidebar-mini');
					misc.sidebar_mini_active = true;
					sideBar.classList.add('addSpace');
					sideBar.classList.remove('removeSpace');
				} else {
					sideBar.classList.remove('addSpace');
					sideBar.classList.add('removeSpace');
				}
			}
			this.screenSize = 'small';
		}
		if (windowWidth >= specificWidth) {
			this.screenSize = 'large';
			if (body.classList.contains('sidebar-mini')) {
				if (this.screenSize === 'small') {
					sideBar.classList.remove('addSpace');
					sideBar.classList.add('removeSpace');
				} else {
					sideBar.classList.remove('addSpace');
					sideBar.classList.add('removeSpace');
				}
			}
		}
	}

	closeSideBar() {
		const body = document.getElementsByTagName('body')[0];
		const sideBar = document.getElementsByClassName('sidebar')[0];

		if (window.innerWidth < 991) {
			sideBar.classList.add('addSpace');
			sideBar.classList.remove('removeSpace');
			setTimeout(function () {
				body.classList.add('sidebar-mini');
				misc.sidebar_mini_active = true;
			}, 300);
		}
	}

	handleUrl(path: any) {
		this.router.navigateByUrl(path).then();
		this.loadFormsDropDown();
	}
}
